<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { authApi } from '@/api'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { getErrorStatus } from '@/utils/error'

const route = useRoute()

type VerifyState = 'loading' | 'success' | 'error'

const state = ref<VerifyState>('loading')
const errorMessage = ref('')

onMounted(async () => {
  const token = route.query.token as string | undefined

  if (!token) {
    state.value = 'error'
    errorMessage.value = 'No verification token found. Please check your email for the correct link.'
    return
  }

  try {
    await authApi.authVerifyEmailGet(token)
    state.value = 'success'
  } catch (e: unknown) {
    state.value = 'error'
    const status = getErrorStatus(e)
    if (status === 401 || status === 400) {
      errorMessage.value = 'This verification link is invalid or has expired. Please request a new one.'
    } else {
      errorMessage.value = 'Something went wrong. Please try again.'
    }
  }
})
</script>

<template>
  <div class="w-full max-w-md text-center">
    <!-- Loading state -->
    <template v-if="state === 'loading'">
      <div class="w-16 h-16 bg-palette-indigo-soft rounded-2xl flex items-center justify-center mx-auto mb-6">
        <font-awesome-icon :icon="['fas', 'spinner']" class="text-3xl text-[var(--primary)] animate-spin" />
      </div>
      <h1 class="text-2xl font-bold text-ink-900 mb-2">Verifying your email…</h1>
      <p class="text-ink-500">Please wait while we verify your email address.</p>
    </template>

    <!-- Success state -->
    <template v-else-if="state === 'success'">
      <div class="w-16 h-16 bg-success-soft rounded-2xl flex items-center justify-center mx-auto mb-6">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="text-3xl text-success-ink" />
      </div>
      <h1 class="text-2xl font-bold text-ink-900 mb-2">Email verified</h1>
      <p class="text-ink-500 mb-8">
        Your email address has been verified. You can now continue using Smart Teacher.
      </p>
      <RouterLink
        to="/login"
        class="inline-flex items-center justify-center w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 transition-colors"
      >
        Continue to app
      </RouterLink>
    </template>

    <!-- Error state -->
    <template v-else>
      <div class="w-16 h-16 bg-danger-soft rounded-2xl flex items-center justify-center mx-auto mb-6">
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-3xl text-danger-ink" />
      </div>
      <h1 class="text-2xl font-bold text-ink-900 mb-2">Verification failed</h1>
      <p class="text-ink-500 mb-6">{{ errorMessage }}</p>
      <RouterLink
        to="/login"
        class="inline-flex items-center justify-center w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 transition-colors"
      >
        Back to sign in
      </RouterLink>
    </template>
  </div>
</template>
